// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a searchable location selector component with autocomplete functionality.
 * It supports both single and multiple location selection, with the ability to create new
 * locations on the fly. The component handles archived and transient locations appropriately,
 * with smart filtering and sorting capabilities.
 */
import { getLocations } from '@/location/api/get-locations';
import {
  CREATE_LOCATION_OPTION_ID,
  LocationAutocomplete,
} from '@/location/components/location-autocomplete';
import { LocationIntegrationStatus } from '@/location/components/location-integration-status';
import type {
  CustomPriorityGroup,
  LocationGroupingMethod,
} from '@/location/components/location-search-options';
import {
  buildGroupedOptions,
  expandLocationsWithZones,
  getGroupLabel,
  isZoneOption,
  locationFilter,
  resolveSelectedZoneName,
  stripZoneMetadata,
} from '@/location/components/location-search-options';
import { getEffectiveAddress } from '@/location/utilities/get-effective-third-party';
import { SparseLocation } from '@/sparse/api/types';
import { AutocompleteValue, FilterOptionsState, Stack } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reject } from 'lodash-es';
import {
  ForwardedRef,
  ReactNode,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Type definition for the component's value, handling both single and multiple selection modes.
 * In multiple mode, returns an array of locations. In single mode, returns a single location or null.
 */
export type LocationSearchValue<Multiple> = Multiple extends true
  ? SparseLocation[]
  : SparseLocation | null;

/**
 * Validates that the grouping array doesn't contain mutually exclusive methods.
 * Throws an error if validation fails.
 */
function validateGrouping(grouping: LocationGroupingMethod[] | undefined) {
  if (!grouping) {
    return;
  }

  const hasInventory = grouping.includes('inventory');
  const hasNonInventory = grouping.includes('non-inventory');
  const hasCustomer = grouping.includes('customer');
  const hasNonCustomer = grouping.includes('non-customer');

  if (hasInventory && hasNonInventory) {
    throw new Error(
      "LocationSearch: 'inventory' and 'non-inventory' grouping methods are mutually exclusive"
    );
  }

  if (hasCustomer && hasNonCustomer) {
    throw new Error(
      "LocationSearch: 'customer' and 'non-customer' grouping methods are mutually exclusive"
    );
  }
}

/**
 * Creates a grouping function for location search results based on the specified grouping methods.
 * Supports hierarchical grouping by combining multiple methods.
 * If a custom priority group is provided, locations matching its IDs will be placed in that group first.
 */
function createGroupBy(
  grouping: LocationGroupingMethod[] | undefined,
  customPriorityGroup?: CustomPriorityGroup
) {
  // If custom priority group is provided, always use grouping
  if (customPriorityGroup) {
    return (option: SparseLocation): string => {
      const checkId = isZoneOption(option) ? option._parentId : option.id;
      if (customPriorityGroup.ids.includes(checkId)) {
        return customPriorityGroup.label;
      }

      if (
        !grouping ||
        grouping.length === 0 ||
        grouping.every((m) => m === 'none')
      ) {
        return 'Other locations';
      }

      return getGroupLabel(option, grouping);
    };
  }

  // No custom priority group — use standard grouping
  if (
    !grouping ||
    grouping.length === 0 ||
    grouping.every((m) => m === 'none')
  ) {
    return undefined;
  }

  return (option: SparseLocation): string => {
    return getGroupLabel(option, grouping);
  };
}

interface Props<Multiple extends boolean | undefined> {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If provided, location options will be grouped by these methods in hierarchical order.
   * Multiple methods can be combined to create nested grouping.
   *
   * @example
   * grouping={['inventory']} // Groups by inventory status
   * grouping={['inventory', 'customer']} // Groups by inventory, then customer within each inventory group
   */
  grouping?: LocationGroupingMethod[];

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, a function used to sort the location options. If not
   * provided, locations will be sorted by archive status and then name.
   */
  sort?: (location: SparseLocation, other: SparseLocation) => number;

  /**
   * If true, multiple values can be selected.
   */
  multiple?: Multiple;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * Optional adornment rendered before the selected location's address in
   * the helper row (e.g. an off-project warning icon).
   */
  helperAdornment?: ReactNode;

  /**
   * The currently selected location(s).
   * Type varies based on whether multiple selection is enabled.
   */
  value: AutocompleteValue<SparseLocation, Multiple, false, true>;

  /**
   * Callback fired when the selected location(s) change.
   * Receives either a single location or an array of locations based on multiple prop.
   */
  onChange: (value: LocationSearchValue<Multiple>) => void;

  /**
   * Callback fired when the user chooses to create a new location.
   * Receives the entered location name as a parameter.
   */
  onCreate: (name: string) => void;

  disabled?: boolean;

  /**
   * Remap is a function that allows locations to be dynamically remapped
   */
  remap?: (location: SparseLocation) => SparseLocation;

  /**
   * If provided, locations belonging to this customer will be moved to the top of the list.
   * This is useful for prioritizing a specific customer's locations in the dropdown.
   * Only effective when using customer grouping.
   * Note: This prop is ignored if customPriorityGroup is provided.
   */
  priorityCustomerId?: string;

  /**
   * If provided, creates a custom priority group that appears first in the dropdown.
   * Locations matching the IDs in this group will be removed from their original groups
   * and displayed together under the custom label. All non-priority locations will be
   * displayed with italic styling.
   * When set, this prop overrides the priorityCustomerId prop.
   */
  customPriorityGroup?: CustomPriorityGroup;

  /**
   * If provided, sets a minimum width for the dropdown popper.
   * Useful when the input field is narrow but options need more space.
   */
  popperWidth?: number;

  /**
   * If true, the selected value's helper row shows the zone name before the
   * address. Used where the input is too narrow to show the full
   * "Parent — Zone" label (e.g. the multi-move grid).
   */
  compact?: boolean;
}

/**
 * A controlled component for searching and selecting locations with autocomplete support.
 *
 * Features:
 * - Supports both single and multiple location selection
 * - Provides real-time search with smart filtering
 * - Allows creation of new locations directly from search
 * - Optional grouping of results by customer
 * - Customizable sorting of location options
 * - Handles archived and transient locations appropriately
 * - Integrates with form libraries via ref forwarding
 */
function LocationSearchRenderer<Multiple extends boolean = false>(
  props: Props<Multiple>,
  ref: ForwardedRef<HTMLInputElement>
) {
  validateGrouping(props.grouping);

  // Fetch all locations, including archived and transient
  const { data, isLoading } = useQuery({
    queryKey: ['location'],
    queryFn: () => getLocations({ isArchived: 'all', isTransient: 'all' }),
  });

  // Allow for optional overriding of the label values
  const remappedLocations = useMemo(() => {
    if (props.remap) {
      return (data?.locations ?? []).map((loc) => props.remap!({ ...loc }));
    }
    return data?.locations ?? [];
  }, [data?.locations, props.remap]);

  // Expand sites with zones into individual zone options, then build grouped + sorted options
  const expanded = useMemo(
    () => expandLocationsWithZones(remappedLocations),
    [remappedLocations]
  );

  const locations = useMemo(
    () =>
      buildGroupedOptions(expanded, {
        grouping: props.grouping,
        customPriorityGroup: props.customPriorityGroup,
        priorityCustomerId: props.priorityCustomerId,
        sort: props.sort,
      }),
    [
      expanded,
      props.grouping,
      props.customPriorityGroup,
      props.priorityCustomerId,
      props.sort,
    ]
  );

  const groupBy = createGroupBy(props.grouping, props.customPriorityGroup);

  /**
   * Custom filter function for location options.
   * Implements special handling for:
   * - Empty input (shows only active, non-transient locations)
   * - Exact matches (prevents duplicate "create" option)
   * - Non-matches (adds "create new" option)
   */
  const filterOptions = (
    options: SparseLocation[],
    state: FilterOptionsState<SparseLocation>
  ) => {
    if (state.inputValue === '') {
      const filtered = locationFilter(options, state);
      return reject(
        filtered,
        ({ isTransient, isArchived }) => isTransient || isArchived
      );
    }

    const filtered = locationFilter(options, state);

    const isMatch = filtered.some(({ name }) => {
      return name.toLowerCase() === state.inputValue.toLowerCase();
    });

    if (isMatch) {
      return filtered;
    }

    const option: SparseLocation = {
      id: CREATE_LOCATION_OPTION_ID,
      key: CREATE_LOCATION_OPTION_ID,
      name: state.inputValue,
      type: 'SITE',
      customer: null,
      isArchived: false,
      isInventory: false,
      isTransient: false,
      isThirdPartyEnabled: false,
      thirdPartyIdentifier: null,
      address: null,
      latitude: '',
      longitude: '',
      googlePlaceId: '',
      description: null,
      isInventoryOverride: false,
      consignee: null,
      zoneCount: 0,
      archivedZoneCount: 0,
      zones: [],
    };

    return [...filtered, option];
  };

  /**
   * Handles changes when multiple selection is enabled.
   */
  const handleChangeLocations = (
    locations: AutocompleteValue<SparseLocation, true, false, true>
  ) => {
    const create = locations.find((location) => {
      if (typeof location === 'string') {
        return true;
      }

      return location.id === CREATE_LOCATION_OPTION_ID;
    });

    if (create === undefined) {
      const cleaned = locations.map((location) =>
        typeof location === 'string' ? location : stripZoneMetadata(location)
      );
      // @ts-expect-error - This callback is only invoked when props.multiple
      // is true, but TypeScript does not know this.
      return props.onChange(cleaned);
    }

    if (typeof create === 'string') {
      return props.onCreate(create);
    }

    if (create.id === CREATE_LOCATION_OPTION_ID) {
      return props.onCreate(create.name);
    }

    // @ts-expect-error - This callback is only invoked when props.multiple is
    // true, but TypeScript does not know this.
    props.onChange(value);
  };

  /**
   * Handles changes when single selection is enabled.
   */
  const handleChangeLocation = (location: SparseLocation | string | null) => {
    if (typeof location === 'string') {
      return props.onCreate(location);
    }

    if (location === null) {
      // @ts-expect-error - This callback is only invoked when props.multiple
      // is false, but TypeScript does not know this.
      return props.onChange(null);
    }

    if (location.id === CREATE_LOCATION_OPTION_ID) {
      return props.onCreate(location.name);
    }

    // @ts-expect-error - This callback is only invoked when props.multiple is
    // false, but TypeScript does not know this.
    props.onChange(stripZoneMetadata(location));
  };

  /**
   * Main change handler that routes to appropriate sub-handler based on selection mode.
   */
  const handleChange = (
    _: unknown,
    value: AutocompleteValue<SparseLocation, Multiple, false, true>
  ) => {
    if (Array.isArray(value)) {
      return handleChangeLocations(value);
    }

    handleChangeLocation(value);
  };

  const queryClient = useQueryClient();

  // Controlled input text using MUI's inputValue + onInputChange pattern.
  // The displayed text is sourced from the latest fetched data by ID, so
  // when the locations query invalidates (e.g. after a drift Pull renames
  // the location) the input reflects the new name without needing the
  // parent form to refresh its stored value. Falls back to the form value
  // snapshot when the location is not in the fetched list.
  const [inputValue, setInputValue] = useState('');
  useEffect(() => {
    if (props.multiple) {
      return;
    }
    const selected = props.value as SparseLocation | null;
    if (!selected) {
      setInputValue('');
      return;
    }
    const fresh = data?.locations.find((loc) => loc.id === selected.id);
    setInputValue(fresh?.name ?? selected.name);
  }, [props.value, data?.locations, props.multiple]);

  // Compute dynamic helper text based on selected value. Returns a plain
  // string in most cases; switches to JSX when a single selected location
  // can be paired with the SFDC out-of-sync chip (right-aligned in the
  // helper row, opens the drift popover on click).
  const computedHelperText = ((): ReactNode => {
    if (props.helperText) {
      return props.helperText;
    }

    if (props.multiple) {
      return 'Multiple locations selected';
    }

    const selectedLocation = props.value as SparseLocation | null;

    if (selectedLocation === null) {
      return 'Not set';
    }

    // The autocomplete is typed against SparseLocation, but the underlying
    // data fetched via getLocations is the full Location with integrations.
    // Re-attach the full row by ID so the address + chip read live data.
    const fullLocation = data?.locations.find(
      (loc) => loc.id === selectedLocation.id
    );
    // For zones, fall back to the parent site's address
    const effectiveAddress = getEffectiveAddress(
      fullLocation ?? selectedLocation
    );
    const addressText =
      effectiveAddress && effectiveAddress.trim() !== ''
        ? effectiveAddress
        : 'This location has no address';

    const addressNode = props.helperAdornment ? (
      <Stack component="span" direction="row" alignItems="center" spacing={0.5}>
        {props.helperAdornment}
        <span>{addressText}</span>
      </Stack>
    ) : (
      <span>{addressText}</span>
    );

    const zoneName = props.compact
      ? resolveSelectedZoneName(selectedLocation, data?.locations ?? [])
      : null;
    const zoneNode = zoneName ? (
      <span>
        <b>Zone:</b> {zoneName}
      </span>
    ) : null;

    const addressRow = !fullLocation ? (
      addressNode
    ) : (
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        {addressNode}
        <LocationIntegrationStatus
          location={fullLocation}
          variant="text"
          onResolved={() => {
            void queryClient.invalidateQueries({ queryKey: ['location'] });
          }}
        />
      </Stack>
    );

    if (!zoneNode) {
      return addressRow;
    }

    return (
      <Stack component="span" spacing={0}>
        {zoneNode}
        {addressRow}
      </Stack>
    );
  })();

  return (
    <LocationAutocomplete
      disabled={props.disabled}
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      loading={isLoading}
      options={locations}
      groupBy={groupBy}
      getOptionDisabled={(option) =>
        isZoneOption(option) && option._isZoneParent
      }
      helperText={computedHelperText}
      filterOptions={filterOptions}
      filterSelectedOptions={props.multiple}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_, newValue, reason) => {
        // MUI fires reason='reset' on blur and after value changes, passing
        // getOptionLabel(value). Because the form's value is a pick-time
        // snapshot, the reset would clobber a refreshed name with the
        // stale one. Re-resolve the name from the latest fetched data.
        if (reason === 'reset' && !props.multiple) {
          const selected = props.value as SparseLocation | null;
          const fresh =
            selected && data?.locations.find((loc) => loc.id === selected.id);
          if (fresh) {
            setInputValue(fresh.name);
            return;
          }
        }
        setInputValue(newValue);
      }}
      multiple={props.multiple}
      priorityLocationIds={props.customPriorityGroup?.ids}
      loadingText="Loading..."
      popperWidth={props.popperWidth}
    />
  );
}

/**
 * A controlled component for selecting a location from a list of options from
 * the remote API.
 */
export const LocationSearch = forwardRef(LocationSearchRenderer);
